import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "../../_css/ChatPopup.module.css";
import helpStyles from "../../_css/HelpAndSupportModal.module.css";
import { X, ArrowUp } from "lucide-react";
import { useSelector } from "react-redux";
import { HELP_AND_SUPPORT_DATA, DETAILED_ANSWERS, CONTEXTUAL_MESSAGES, FALLBACK_CONTEXTUAL_MESSAGES } from "../../data/helpAndSupportData";
import { CustomButton } from "../UI/CustomButtons";

// const AI_AVATAR = `${process.env.PUBLIC_URL}/img/user-icon.png`;
const DR_AVATAR = `${process.env.PUBLIC_URL}/img/askSalah.png`;

const HelpAndSupportModal = ({ show, onClose }) => {
  const lang = useSelector((state) => state.language.lang);
  const {
    title,
    initialMessage,
    contactSupportMessage,
    ticketCreatedMessage,
    inputPlaceholder,
    sendButton,
    responseTime,
    predefinedQuestions,
  } = HELP_AND_SUPPORT_DATA[lang];

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [ticketNumber, setTicketNumber] = useState(null);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const userInfo = JSON.parse(sessionStorage.getItem("sal_userInfo"));
  const userName = userInfo?.fullName || "User";

  // Initialize with welcome message
  useEffect(() => {
    if (show && messages.length === 0) {
      const welcomeMessage = {
        sender: "ai",
        text: initialMessage.replace("{name}", userName),
        isInitial: true,
        predefinedQuestions: predefinedQuestions,
      };
      setMessages([welcomeMessage]);
    }
  }, [show, initialMessage, userName, predefinedQuestions, messages.length]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [messages]);

  // Keyboard detection for mobile
  useEffect(() => {
    if (!show) return;

    const handleResize = () => {
      const initialViewportHeight = window.innerHeight;
      const currentViewportHeight =
        window.visualViewport?.height || window.innerHeight;

      // If viewport height decreased significantly, keyboard is likely open
      const heightDifference = initialViewportHeight - currentViewportHeight;
      setIsKeyboardOpen(heightDifference > 150);
    };

    const handleVisualViewportChange = () => {
      if (window.visualViewport) {
        const heightDifference =
          window.innerHeight - window.visualViewport.height;
        setIsKeyboardOpen(heightDifference > 150);
      }
    };

    // Listen for viewport changes
    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        "resize",
        handleVisualViewportChange
      );
    } else {
      window.addEventListener("resize", handleResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          handleVisualViewportChange
        );
      } else {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, [show]);

  const getDetailedAnswer = (questionId) => {
    return DETAILED_ANSWERS[lang]?.[questionId] || "";
  };

  const handlePredefinedQuestion = (question) => {
    // Add user's selection
    const userMessage = {
      sender: userName,
      text: question.text,
      isPredefined: true,
    };

    // Get detailed answer for follow-up questions
    let answer = question.answer;
    if (question.id && getDetailedAnswer(question.id)) {
      answer = getDetailedAnswer(question.id);
    }

    // Create contextual follow-up message
    const getContextualMessage = (category, hasFollowUps) => {
      if (!hasFollowUps) return "";
      
      return CONTEXTUAL_MESSAGES[lang]?.[category] || 
        FALLBACK_CONTEXTUAL_MESSAGES[lang]?.replace('{category}', category.toLowerCase());
    };

    let contextualMessage = question.followUpQuestions && question.followUpQuestions.length > 0 
      ? `${getContextualMessage(question.text, true)}`
      : "";

    console.log("answer", answer);
    if(!contextualMessage) {
      contextualMessage = answer;
    }
    // Add AI response with follow-up questions
    const aiMessage = {
      sender: "ai",
      text: contextualMessage,
      followUpQuestions: question.followUpQuestions,
    };

    console.log(aiMessage);

    setMessages((prev) => [...prev, userMessage, aiMessage]);
  };

  const handleSend = async () => {
    if (!input.trim() || isSending) return;

    setIsSending(true);

    // Add user message
    const userMessage = {
      sender: userName,
      text: input,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setWaiting(true);

    // Simulate response delay
    setTimeout(() => {
      // Generate ticket number if not exists
      if (!ticketNumber) {
        const newTicketNumber = Math.floor(Math.random() * 9000) + 1000;
        setTicketNumber(newTicketNumber);
      }

      // Add AI response
      const aiMessage = {
        sender: "ai",
        text: ticketNumber
          ? ticketCreatedMessage
          // ? ticketCreatedMessage?.replace("{ticketNumber}", ticketNumber)
          : contactSupportMessage,
      };

      setMessages((prev) => [...prev, aiMessage]);
      setWaiting(false);
      setIsSending(false);
    }, 500);
  };

  const resetStates = () => {
    setMessages([]);
    setInput("");
    setWaiting(false);
    setTicketNumber(null);
    setIsKeyboardOpen(false);
    setIsSending(false);
  };

  useEffect(() => {
    if (!show) {
      resetStates();
    }
  }, [show]);

  return (
    <>
      {show && (
        <div className={styles.overlay}>
          <div
            className={`${styles.popup} ${
              isKeyboardOpen ? styles.keyboardOpen : ""
            }`}
          >
            <div className={styles.header}>
              <h4 className={styles.title}>{title}</h4>
              <button className={styles.closeBtn} onClick={onClose}>
                <X size={22} />
              </button>
            </div>
            <div className={styles.chatBody}>
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={
                    msg.sender === userName ? styles.drMsg : styles.aiMsg
                  }
                >
                  <div className={styles.msgText}>
                    <div className={styles.avatar}>
                      {msg.sender !== userName && (
                        <i
                          style={{
                            borderRadius: "0",
                            background: "none",
                          }}
                        >
                          <img
                            src={DR_AVATAR}
                            alt="avatar"
                            style={{
                              borderRadius: "0",
                              width: "40px",
                              height: "auto",
                            }}
                          />
                        </i>
                      )}
                      <span>{msg.sender === userName ? userName : ""}</span>
                    </div>

                    {msg.isInitial && msg.predefinedQuestions ? (
                      <div>
                        <p className="m-0 mt-2">{msg.text}</p>
                        <div
                          className={`mt-3 ${helpStyles.predefinedQuestionsContainer}`}
                        >
                          {msg.predefinedQuestions.map((question) => (
                            <button
                              key={question.id}
                              className={helpStyles.predefinedQuestionButton}
                              onClick={() => handlePredefinedQuestion(question)}
                            >
                              {question.text}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : msg.followUpQuestions ? (
                      <div>
                        <p className="m-0 mt-2">{msg.text}</p>
                        <div
                          className={`mt-3 ${helpStyles.predefinedQuestionsContainer}`}
                        >
                          {msg.followUpQuestions.map((question) => (
                            <button
                              key={question.id}
                              className={helpStyles.predefinedQuestionButton}
                              onClick={() => handlePredefinedQuestion(question)}
                            >
                              {question.text}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="m-0 mt-2">{msg.text}</p>
                    )}
                  </div>
                </div>
              ))}
              {waiting && (
                <div className={styles.aiMsg}>
                  <div className={styles.msgText}>
                    <div className={styles.avatar}>
                      <i>
                        <img src={DR_AVATAR} alt="avatar" />
                      </i>
                    </div>
                    <div className="d-flex align-items-center">
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <small className="text-muted">Typing...</small>
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <hr className="m-0" />
            <div className={styles.inputRow}>
              <input
                className={styles.input}
                value={input}
                ref={inputRef}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !waiting && !isSending) handleSend();
                }}
                placeholder={inputPlaceholder}
                disabled={waiting || isSending}
              />
              <div className={styles.bottomBar}>
                {/* <div
                
                  className={
                    styles.bottomIconWrap +
                    (waiting || !input.trim() || isSending
                      ? " " + styles.disabled
                      : "")
                  }
                > */}
                <CustomButton
                  text={sendButton}
                  disabled={waiting || !input.trim() || isSending}
                  onClick={handleSend}
                />
                {/* </div> */}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpAndSupportModal;
